import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Boutique IA billing currency. Decided SERVER-SIDE (get-billing-currency, from the
 * visitor's country). The browser only displays it — it never sends it to checkout.
 */
export type Currency = "USD" | "EUR" | "GBP" | "CAD";
export const SUPPORTED_CURRENCIES: Currency[] = ["USD", "EUR", "GBP", "CAD"];

/** USD reference amounts → localized amounts (cents), mirror of the server table. */
type Tiers = { essentiel: number; pro: number; premium: number };
type Prices = { fee: { starter: number; pro: number }; subscription: Tiers; subscriptionYearly?: Tiers };
const USD_PRICES: Prices = { fee: { starter: 2000, pro: 8000 }, subscription: { essentiel: 1900, pro: 3900, premium: 11900 }, subscriptionYearly: { essentiel: 19000, pro: 39000, premium: 119000 } };

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  symbol: string;
  /** Creation fees (USD reference 20 / 80) in the billing currency. */
  formatPrice: (amount: number) => string;
  /** Subscriptions (USD reference 19 / 39 / 119 monthly, 190 / 390 / 1190 yearly) in the billing currency. */
  formatSubscriptionPrice: (amount: number) => string;
  isManual: boolean;
  ready: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const SYMBOLS: Record<Currency, string> = { USD: "$", EUR: "€", GBP: "£", CAD: "$ CA" };

/** Shown instead of any amount that is not in the server grid for a non-USD context. */
export const UNAVAILABLE_PRICE = "—";

export function formatInCurrency(amount: number, currency: Currency) {
  const n = Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace(".", ",");
  if (currency === "EUR") return `${n} €`;
  if (currency === "CAD") return `${n} $ CA`;
  if (currency === "GBP") return `£${n}`;
  if (currency === "USD") return `${n} $`;
  return UNAVAILABLE_PRICE;
}

type BillingInfo = { currency: Currency; prices: Prices };

/** Fetches the server-decided billing currency (optionally locked by a store's paid fee). */
export async function fetchBillingCurrency(storeId?: string | null): Promise<BillingInfo> {
  try {
    const { data } = await supabase.functions.invoke(
      `get-billing-currency${storeId ? `?store_id=${encodeURIComponent(storeId)}` : ""}`,
      { method: "GET" },
    );
    const cur = String(data?.currency || "").toUpperCase() as Currency;
    if (SUPPORTED_CURRENCIES.includes(cur) && data?.prices) return { currency: cur, prices: data.prices };
  } catch { /* ignore */ }
  return { currency: "USD", prices: USD_PRICES };
}

export function makeFormatters(info: BillingInfo) {
  const supported = SUPPORTED_CURRENCIES.includes(info.currency);
  const fee: Record<number, number> = { 20: info.prices.fee.starter, 80: info.prices.fee.pro };
  const sub: Record<number, number> = { 19: info.prices.subscription.essentiel, 39: info.prices.subscription.pro, 119: info.prices.subscription.premium };
  // Yearly amounts only from the server grid (pricesPayload); never computed in the browser.
  const y = info.prices.subscriptionYearly;
  if (y) Object.assign(sub, { 190: y.essentiel, 390: y.pro, 1190: y.premium });
  // Out-of-grid amount: shown as-is only in a USD context; otherwise never a raw USD amount.
  const fmt = (map: Record<number, number>) => (a: number) => {
    if (!supported) return UNAVAILABLE_PRICE;
    if (map[a] !== undefined) return formatInCurrency(map[a] / 100, info.currency);
    return info.currency === "USD" ? formatInCurrency(a, "USD") : UNAVAILABLE_PRICE;
  };
  return { formatPrice: fmt(fee), formatSubscriptionPrice: fmt(sub) };
}

let cached: Promise<BillingInfo> | null = null;

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [info, setInfo] = useState<BillingInfo>({ currency: "USD", prices: USD_PRICES });
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!cached) cached = fetchBillingCurrency();
    cached.then((i) => { setInfo(i); setReady(true); });
  }, []);
  const f = makeFormatters(info);
  return (
    <CurrencyContext.Provider
      value={{ currency: info.currency, setCurrency: () => {}, symbol: SYMBOLS[info.currency], ...f, isManual: false, ready }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (ctx) return ctx;
  const f = makeFormatters({ currency: "USD", prices: USD_PRICES });
  return { currency: "USD" as Currency, setCurrency: () => {}, symbol: "$", ...f, isManual: false, ready: false };
}
